import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { Users, UserPlus, Phone, Trophy, Database, Loader2 } from "lucide-react";

const STATUS_CONFIG = {
  new: { label: "Nouveau", color: "bg-blue-100 text-blue-700" },
  contacted: { label: "Contacte", color: "bg-amber-100 text-amber-700" },
  won: { label: "Gagne", color: "bg-emerald-100 text-emerald-700" },
  lost: { label: "Perdu", color: "bg-red-100 text-red-700" },
};

const CHART_COLORS = ["#336DBF", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
const CHART_TICK = { fontSize: 12, fill: "#64748B" };
const BAR_RADIUS = [4, 4, 0, 0];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([
        api.get("/stats"),
        api.get("/leads"),
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.slice(0, 5));
    } catch (err) {
      console.error("Erreur chargement:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post("/seed");
      toast.success(
        `Donnees initialisees : ${res.data.services_created} services, ${res.data.villes_created} villes, ${res.data.avis_created} avis`
      );
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de l'initialisation");
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#336DBF]" />
      </div>
    );
  }

  const sourceData = stats
    ? Object.entries(stats.leads_by_source).map(([name, count]) => ({ name, count }))
    : [];
  const villeData = stats
    ? Object.entries(stats.leads_by_ville).map(([name, count]) => ({ name, count }))
    : [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
            Tableau de bord
          </h1>
          <p className="text-sm text-slate-500 mt-1">Vue d'ensemble de votre activité</p>
        </div>
        <Button
          onClick={handleSeed}
          disabled={seeding}
          variant="outline"
          className="gap-2"
          data-testid="seed-button"
        >
          {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
          Initialiser les donnees
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200" data-testid="stat-total-leads">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Leads</p>
                <p className="text-3xl font-heading font-bold text-slate-900 mt-1">
                  {stats?.total_leads || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-md bg-[#336DBF]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#336DBF]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="stat-new-leads">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nouveaux</p>
                <p className="text-3xl font-heading font-bold text-blue-600 mt-1">
                  {stats?.leads_by_status?.new || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="stat-contacted-leads">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contactes</p>
                <p className="text-3xl font-heading font-bold text-amber-600 mt-1">
                  {stats?.leads_by_status?.contacted || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-md bg-amber-50 flex items-center justify-center">
                <Phone className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="stat-won-leads">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Gagnes</p>
                <p className="text-3xl font-heading font-bold text-emerald-600 mt-1">
                  {stats?.leads_by_status?.won || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base font-semibold text-slate-900">
              Leads par source
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sourceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={sourceData} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={CHART_TICK} />
                  <YAxis tick={CHART_TICK} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" radius={BAR_RADIUS}>
                    {sourceData.map((entry) => (
                      <Cell key={entry.name} fill={CHART_COLORS[sourceData.indexOf(entry) % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 py-8 text-center">
                Aucune donnee disponible
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base font-semibold text-slate-900">
              Leads par ville
            </CardTitle>
          </CardHeader>
          <CardContent>
            {villeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={villeData} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={CHART_TICK} />
                  <YAxis tick={CHART_TICK} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#336DBF" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 py-8 text-center">
                Aucune donnee disponible
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Leads */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base font-semibold text-slate-900">
            Leads recents
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentLeads.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nom</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Service</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Ville</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Source</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Statut</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentLeads.map((lead) => (
                  <TableRow key={lead.id} className="hover:bg-slate-50/50">
                    <TableCell className="text-sm font-medium text-slate-900">{lead.nom}</TableCell>
                    <TableCell className="text-sm text-slate-600">{lead.service}</TableCell>
                    <TableCell className="text-sm text-slate-600">{lead.ville}</TableCell>
                    <TableCell className="text-sm text-slate-600">{lead.source}</TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={STATUS_CONFIG[lead.status]?.color || "bg-slate-100 text-slate-600"}
                      >
                        {STATUS_CONFIG[lead.status]?.label || lead.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString("fr-CH") : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center" data-testid="no-leads-message">
              Aucun lead pour le moment. Creez des leads via l'API ou le formulaire public.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
